import { useMemo } from 'react';
import { OBJ, TOOLS } from '../constants';
import { clearMarkStyle, markRange, runStyleOf, styleOfSelection } from '../engine/richText';
import { docFlowList, findSelected, orderDocSelection, usePdfEditorStore } from '../state/usePdfEditorStore';
import { refocusTextEditor } from '../ui/prims';

/**
 * Where a formatting change goes, and what the controls should read back.
 *
 * Two surfaces ask this now — the ribbon's font and paragraph groups, and the
 * inspector's property list — and the answer is never simply "the selected
 * object". Bold pressed with three words highlighted marks those three words;
 * pressed with the box merely selected it marks the box; pressed with nothing
 * selected at all it changes what the text tool will produce next. That
 * decision lives here once, so the two surfaces cannot drift apart on it.
 */
export function useFormatting() {
    const activeTool = usePdfEditorStore((s) => s.activeTool);
    const toolSettings = usePdfEditorStore((s) => s.toolSettings);
    const updateToolSettings = usePdfEditorStore((s) => s.updateToolSettings);
    const selectedId = usePdfEditorStore((s) => s.selectedId);
    const pages = usePdfEditorStore((s) => s.pages);
    const objectsByUid = usePdfEditorStore((s) => s.objectsByUid);
    const updateObject = usePdfEditorStore((s) => s.updateObject);
    const setTool = usePdfEditorStore((s) => s.setTool);
    const editingId = usePdfEditorStore((s) => s.editingId);
    const textSelection = usePdfEditorStore((s) => s.textSelection);
    const editingCell = usePdfEditorStore((s) => s.editingCell);
    const docSelection = usePdfEditorStore((s) => s.docSelection);
    const beginGesture = usePdfEditorStore((s) => s.beginGesture);

    // Document mode: a selection across paragraphs, as the part of each box it
    // covers. Formatting then goes to every one of them.
    const docRanges = useMemo(() => {
        if (!docSelection) return null;
        const list = docFlowList({ pages, objectsByUid });
        const ends = orderDocSelection(docSelection, list);
        if (!ends) return null;
        const out = [];
        for (let i = ends.from.i; i <= ends.to.i; i += 1) {
            const { uid: at, obj } = list[i];
            const len = String(obj.text ?? '').length;
            out.push({
                uid: at,
                obj,
                start: i === ends.from.i ? ends.from.off : 0,
                end: i === ends.to.i ? ends.to.off : len,
            });
        }
        return out;
    }, [docSelection, pages, objectsByUid]);

    const selection = useMemo(
        () => findSelected({ selectedId, pages, objectsByUid }),
        [selectedId, pages, objectsByUid],
    );
    const object = selection?.object || null;
    const uid = selection?.uid;

    // Styling applies to the selected TEXT, when there is a selection inside the
    // box being edited — that is what a user expects after highlighting a word.
    // Alignment, line height and the background stay box-level: they describe
    // the block, not a run.
    const runSelection = editingId && object?.id === editingId && textSelection.end > textSelection.start
        ? textSelection
        : null;

    /** Write to the selection, the selected object, or the armed tool's defaults. */
    // A table cell being typed in takes formatting like a paragraph does in Word:
    // bold, italic, size and alignment for that cell (`cellStyles`); the colour
    // and the font belong to the whole table, which draws every cell in one.
    const tableCell = object?.type === OBJ.TABLE && editingId === object.id && editingCell ? editingCell : null;

    const write = (toolKey, patch) => {
        if (tableCell) {
            const CELL_KEYS = ['bold', 'italic', 'align', 'fontSize', 'valign'];
            const TABLE_KEYS = ['color', 'fontFamily', 'fontStack', 'fontLabel', 'fontId'];
            const key = `${tableCell.r},${tableCell.c}`;
            const own = object.cellStyles?.[key] || {};
            const cellPart = Object.fromEntries(Object.entries(patch).filter(([k]) => CELL_KEYS.includes(k)));
            const tablePart = Object.fromEntries(Object.entries(patch).filter(([k]) => TABLE_KEYS.includes(k)));
            const next = { ...tablePart };
            if (Object.keys(cellPart).length) next.cellStyles = { ...(object.cellStyles || {}), [key]: { ...own, ...cellPart } };
            if (Object.keys(next).length) updateObject(uid, object.id, next);
            refocusTextEditor();
            return;
        }
        if (docRanges?.length) {
            // One undo step for the lot; each box takes the run style over its
            // own part of the selection, and the box-level keys (alignment,
            // line spacing, background) on the whole box.
            const runPatch = runStyleOf(patch);
            const runKeys = Object.keys(runPatch);
            const boxPatch = { ...patch };
            for (const key of runKeys) delete boxPatch[key];
            beginGesture();
            for (const r of docRanges) {
                // Each update can re-flow the document and carry later
                // paragraphs onto another page, so find the box where it is now.
                const live = docFlowList(usePdfEditorStore.getState()).find((e) => e.obj.id === r.obj.id);
                if (!live) continue;
                const next = { ...boxPatch };
                if (runKeys.length && r.end > r.start) next.marks = markRange(live.obj, r.start, r.end, runPatch);
                if (Object.keys(next).length) updateObject(live.uid, live.obj.id, next, { transient: true });
            }
            return;
        }
        if (object) {
            const runPatch = runStyleOf(patch);
            const runKeys = Object.keys(runPatch);
            const boxPatch = { ...patch };
            for (const key of runKeys) delete boxPatch[key];

            if (runSelection && runKeys.length) {
                // Part of the box: the patch lands on that range's marks and the
                // box's own style is left alone.
                updateObject(uid, object.id, {
                    ...boxPatch,
                    marks: markRange(object, runSelection.start, runSelection.end, runPatch),
                });
            } else if (runKeys.length && object.marks?.length) {
                // The WHOLE box, over text that carries marks. Setting the box
                // style alone would change nothing visible, because a mark's
                // style wins over the box's — that is what made a colour swatch
                // look dead until the text was highlighted first. So the marks
                // give up exactly the properties being set, and keep the rest.
                updateObject(uid, object.id, {
                    ...patch,
                    marks: clearMarkStyle(object, runKeys),
                });
            } else {
                updateObject(uid, object.id, patch);
            }
        } else {
            updateToolSettings(toolKey, patch);
            // Styling with nothing selected describes the next box, so arm the
            // tool that makes one: otherwise picking a style from the ribbon
            // changes a number no one can see and reads as a dead button.
            if (toolKey === 'text' && activeTool !== TOOLS.TEXT) setTool(TOOLS.TEXT);
        }
        // A control that had to take focus (a <select>, a colour field) hands
        // the caret back, so typing carries straight on.
        refocusTextEditor();
    };

    /** True when text formatting applies at all: a text box, or the text tool. */
    const textish = object?.type === OBJ.TEXT || !!tableCell || (!object && activeTool === TOOLS.TEXT);

    /**
     * The style the text controls should show. Highlighting a bold word shows
     * Bold as active even when the rest of the box is regular.
     */
    const firstRange = docRanges?.find((r) => r.end > r.start) || docRanges?.[0];
    let textSource = null;
    if (firstRange) {
        // Across paragraphs: what the first selected character carries.
        textSource = styleOfSelection(firstRange.obj, firstRange.start, Math.max(firstRange.start + 1, firstRange.end));
    } else if (tableCell) {
        const own = object.cellStyles?.[`${tableCell.r},${tableCell.c}`] || {};
        textSource = {
            ...object,
            fontSize: own.fontSize ?? object.fontSize,
            bold: !!own.bold,
            italic: !!own.italic,
            align: own.align ?? object.align,
            underline: false,
        };
    } else if (textish) {
        textSource = object
            ? (runSelection ? styleOfSelection(object, runSelection.start, runSelection.end) : object)
            : toolSettings.text;
    }

    /**
     * What the ribbon's always-on font groups read.
     *
     * Wider than `textSource`: with nothing selected at all they fall back to
     * the text tool's defaults rather than going dark, because that is where
     * `write` sends them. Only another kind of object — a shape, an image —
     * leaves them nothing to describe. The options bar keeps the narrow flag;
     * it returns early on `textish` and would show text options for the pencil.
     */
    const textStyle = textSource || (object ? null : toolSettings.text);

    return {
        activeTool, toolSettings, object, uid, runSelection, write,
        textish, textSource, textStyle,
    };
}
